<template>
  <v-card outlined>
    <v-card-title>
      Urlopy
      <v-spacer />
      <v-btn small color="primary" class="text-none" @click="dialog = true">
        <v-icon left small>mdi-beach</v-icon> Złóż wniosek
      </v-btn>
    </v-card-title>
    <v-card-text>
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary" size="28" />
      </div>
      <template v-else>
        <!-- Pula -->
        <div class="d-flex align-center flex-wrap mb-1">
          <div class="mr-6">
            <div class="text-h5 font-weight-bold">
              {{ fmtPool(allowance.remainingHours) }}
              <span class="text-body-2 grey--text">/ {{ allowance.totalDays }} dni</span>
            </div>
            <div class="grey--text text-caption">pozostało w puli na {{ allowance.year }} r.</div>
          </div>
          <div style="flex: 1; min-width: 180px">
            <v-progress-linear
              :value="allowance.totalHours ? (allowance.usedHours / allowance.totalHours) * 100 : 0"
              height="10" rounded color="primary"
            />
            <div class="text-caption grey--text mt-1">wykorzystane: {{ fmtPool(allowance.usedHours) }}</div>
          </div>
        </div>
        <v-alert v-if="!allowance.totalDays" dense text type="info" class="mt-2 mb-0">
          Nie masz jeszcze przydzielonej puli dni — pulę ustala właściciel w zakładce Kadry.
        </v-alert>

        <!-- Moje wnioski -->
        <v-divider class="my-4" />
        <div class="text-subtitle-2 mb-2">Moje wnioski</div>
        <div v-if="requests.length === 0" class="grey--text text-body-2">Brak wniosków.</div>
        <v-simple-table v-else dense>
          <tbody>
            <tr v-for="r in requests" :key="r.id">
              <td class="text-no-wrap">{{ fmt(r.startDate) }} – {{ fmt(r.endDate) }}</td>
              <td>{{ typeLabel(r.type) }}</td>
              <td class="text-no-wrap">{{ r.type === 'HOURLY' ? r.hours + ' godz.' : r.days + ' dni' }}</td>
              <td>
                <v-chip x-small :color="statusColor(r.status)" dark>{{ statusLabel(r.status) }}</v-chip>
              </td>
              <td class="text-right">
                <v-btn v-if="r.status === 'PENDING'" x-small text color="error" @click="cancel(r)">Anuluj</v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </template>
    </v-card-text>

    <!-- Dialog wniosku -->
    <v-dialog v-model="dialog" max-width="440">
      <v-card>
        <v-card-title>Wniosek urlopowy</v-card-title>
        <v-card-text>
          <v-select
            v-model="form.type" :items="typeOptions" label="Rodzaj urlopu"
            dense outlined item-text="text" item-value="value"
          />
          <v-text-field v-model="form.startDate" :label="form.type === 'HOURLY' ? 'Dzień' : 'Od'" type="date" dense outlined />
          <v-text-field v-if="form.type !== 'HOURLY'" v-model="form.endDate" label="Do" type="date" dense outlined />
          <v-select
            v-if="form.type === 'HOURLY'"
            v-model="form.hours" :items="[1,2,3,4,5,6,7]"
            label="Liczba godzin" dense outlined
          />
          <v-textarea v-model="form.note" label="Uwagi (opcjonalnie)" rows="2" dense outlined />
          <div class="text-caption grey--text">Liczba dni liczona jest z dni roboczych (pon.–pt., bez świąt ustawowych).</div>
          <v-alert v-if="conflicts.length" dense text type="warning" class="mt-3 mb-0">
            W tym terminie urlop ma już:
            <div v-for="(c, i) in conflicts" :key="i" class="text-caption">
              • {{ c.name }} — {{ fmt(c.startDate) }} – {{ fmt(c.endDate) }} ({{ statusLabel(c.status) }})
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!form.startDate || (form.type !== 'HOURLY' && !form.endDate)" @click="submit">Wyślij wniosek</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { LEAVE_TYPES, leaveTypeLabel, leaveStatusLabel, leaveStatusColor, fmtPoolHours } from '@/services/leaveDicts';

export default {
  name: 'LeaveSection',
  data() {
    return {
      loading: true,
      saving: false,
      dialog: false,
      allowance: { year: new Date().getFullYear(), totalDays: 0, usedDays: 0, remainingDays: 0 },
      requests: [],
      form: { type: 'VACATION', startDate: '', endDate: '', note: '', hours: 1 },
      typeOptions: LEAVE_TYPES,
      conflicts: [],
    };
  },
  async created() {
    await this.load();
  },
  watch: {
    'form.startDate'() { this.checkConflicts(); },
    'form.endDate'() { this.checkConflicts(); },
    dialog(v) { if (!v) this.conflicts = []; },
  },
  methods: {
    getConfig() {
      return { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
    },
    async load() {
      this.loading = true;
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'hr/me', this.getConfig());
        const data = res.data?.data || {};
        if (data.allowance) this.allowance = data.allowance;
        this.requests = data.requests || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async checkConflicts() {
      const s = this.form.startDate;
      if (!s) { this.conflicts = []; return; }
      const e = this.form.type === 'HOURLY' ? s : (this.form.endDate || s);
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + `hr/conflicts?startDate=${s}&endDate=${e}`,
          this.getConfig(),
        );
        this.conflicts = res.data?.data || [];
      } catch (err) {
        this.conflicts = [];
      }
    },
    async submit() {
      this.saving = true;
      try {
        const payload = { ...this.form };
        if (payload.type === 'HOURLY') payload.endDate = payload.startDate;
        else delete payload.hours;
        await axios.post(process.env.VUE_APP_ROOT_API + 'hr/requests', payload, this.getConfig());
        this.dialog = false;
        this.form = { type: 'VACATION', startDate: '', endDate: '', note: '', hours: 1 };
        await this.load();
        Swal.fire('Wysłano', 'Wniosek czeka na decyzję właściciela.', 'success');
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || 'Nie udało się złożyć wniosku', 'error');
      } finally {
        this.saving = false;
      }
    },
    async cancel(r) {
      try {
        await axios.delete(process.env.VUE_APP_ROOT_API + 'hr/requests/' + r.id, this.getConfig());
        await this.load();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message || 'Nie udało się anulować', 'error');
      }
    },
    fmt(d) { return new Date(d).toLocaleDateString('pl-PL'); },
    typeLabel: leaveTypeLabel,
    fmtPool: fmtPoolHours,
    statusLabel: leaveStatusLabel,
    statusColor: leaveStatusColor,
  },
};
</script>
